import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const PortalManagement = () => {
    const [portalClients, setPortalClients] = useState([]);
    const [pendingUploads, setPendingUploads] = useState([]);
    const [unreadMessages, setUnreadMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [clients, setClients] = useState([]);

    const [inviteForm, setInviteForm] = useState({
        client_id: '',
        send_invitation: true,
        custom_message: '',
        can_view_documents: true,
        can_upload_documents: true,
        can_view_billing: true,
        can_send_messages: true,
        can_view_calendar: true
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const token = localStorage.getItem('access_token');

            // Fetch portal clients
            const clientsRes = await fetch('/api/portal/manage/access/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const clientsData = await clientsRes.json();
            setPortalClients(Array.isArray(clientsData) ? clientsData : clientsData.results || []);

            // Fetch pending uploads
            const uploadsRes = await fetch('/api/portal/manage/uploads/?status=PENDING', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const uploadsData = await uploadsRes.json();
            setPendingUploads(Array.isArray(uploadsData) ? uploadsData : uploadsData.results || []);

            // Fetch unread messages
            const messagesRes = await fetch('/api/portal/manage/messages/?unread=true', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const messagesData = await messagesRes.json();
            setUnreadMessages(Array.isArray(messagesData) ? messagesData : messagesData.results || []);

        } catch (err) {
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch('/api/clients/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setClients(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            console.error('Error fetching clients:', err);
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch('/api/portal/manage/access/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(inviteForm)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to send invitation');
            }

            setShowInviteModal(false);
            setInviteForm({
                client_id: '',
                send_invitation: true,
                custom_message: '',
                can_view_documents: true,
                can_upload_documents: true,
                can_view_billing: true,
                can_send_messages: true,
                can_view_calendar: true
            });
            fetchData();
        } catch (err) {
            alert(err.message);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ACTIVE': return 'bg-green-100 text-green-800';
            case 'PENDING': return 'bg-yellow-100 text-yellow-800';
            case 'SUSPENDED': return 'bg-red-100 text-red-800';
            case 'REVOKED': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Client Portal</h1>
                <p className="text-gray-600 mt-1">Manage client portal access and communications</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-blue-100 rounded-lg">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Portal Clients</p>
                            <p className="text-2xl font-semibold text-gray-900">{portalClients.length}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-green-100 rounded-lg">
                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Active</p>
                            <p className="text-2xl font-semibold text-gray-900">
                                {portalClients.filter(c => c.status === 'ACTIVE').length}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-yellow-100 rounded-lg">
                            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Pending Uploads</p>
                            <p className="text-2xl font-semibold text-gray-900">{pendingUploads.length}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-purple-100 rounded-lg">
                            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Unread Messages</p>
                            <p className="text-2xl font-semibold text-gray-900">{unreadMessages.length}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <button
                    onClick={() => {
                        fetchClients();
                        setShowInviteModal(true);
                    }}
                    className="flex items-center justify-center p-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                    Invite Client to Portal
                </button>
                <Link
                    to="/portal/messages"
                    className="flex items-center justify-center p-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                    View Messages
                </Link>
                <Link
                    to="/portal/uploads"
                    className="flex items-center justify-center p-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Review Uploads
                </Link>
            </div>

            {/* Portal Clients Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
                <div className="p-6 border-b border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900">Portal Clients</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Last Login</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Permissions</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {portalClients.length > 0 ? (
                                portalClients.map((access) => (
                                    <tr key={access.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="font-medium text-gray-900">{access.client_name}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                            {access.client_email}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(access.status)}`}>
                                                {access.status_display}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                                            {access.last_login
                                                ? new Date(access.last_login).toLocaleDateString()
                                                : 'Never'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <div className="flex justify-center gap-1">
                                                {access.can_view_documents && (
                                                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">Docs</span>
                                                )}
                                                {access.can_send_messages && (
                                                    <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">Msg</span>
                                                )}
                                                {access.can_view_billing && (
                                                    <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">Bill</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                            <Link
                                                to={`/portal/clients/${access.id}`}
                                                className="text-blue-600 hover:text-blue-700"
                                            >
                                                Manage
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                        No clients have portal access yet
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Invite Modal */}
            {showInviteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-gray-900">Invite Client to Portal</h2>
                                <button onClick={() => setShowInviteModal(false)} className="text-gray-400 hover:text-gray-600">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleInvite} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Select Client *</label>
                                <select
                                    required
                                    value={inviteForm.client_id}
                                    onChange={(e) => setInviteForm({ ...inviteForm, client_id: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                >
                                    <option value="">Choose a client</option>
                                    {clients.map(client => (
                                        <option key={client.id} value={client.id}>
                                            {client.first_name} {client.last_name} ({client.email})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                                <div className="space-y-2">
                                    {[
                                        { key: 'can_view_documents', label: 'View Documents' },
                                        { key: 'can_upload_documents', label: 'Upload Documents' },
                                        { key: 'can_view_billing', label: 'View Billing' },
                                        { key: 'can_send_messages', label: 'Send Messages' },
                                        { key: 'can_view_calendar', label: 'View Calendar' }
                                    ].map(perm => (
                                        <label key={perm.key} className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={inviteForm[perm.key]}
                                                onChange={(e) => setInviteForm({ ...inviteForm, [perm.key]: e.target.checked })}
                                                className="rounded border-gray-300 text-blue-600"
                                            />
                                            <span className="ml-2 text-sm text-gray-700">{perm.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Message (Optional)</label>
                                <textarea
                                    value={inviteForm.custom_message}
                                    onChange={(e) => setInviteForm({ ...inviteForm, custom_message: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    rows="3"
                                    placeholder="Add a personal message to the invitation email"
                                ></textarea>
                            </div>

                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={inviteForm.send_invitation}
                                    onChange={(e) => setInviteForm({ ...inviteForm, send_invitation: e.target.checked })}
                                    className="rounded border-gray-300 text-blue-600"
                                />
                                <span className="ml-2 text-sm text-gray-700">Send invitation email now</span>
                            </label>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => setShowInviteModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Send Invitation
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PortalManagement;
